import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CapsuleScene } from './CapsuleScene';
import { loaderRegistry } from './loaderRegistry';
import { SHOW_DELAY_MS, EXIT_FADE_MS, POP_MS, POOF_CLEAR_MS, QUIET_ROUTES } from './capsuleConfig';

const SETTLE_MS = 120;

// Single host for every capsule loader.
// Site open + episode page: full sequence (Capsule Corp screen if still loading at the pop).
// Other pages: capsule drop + poof straight into the page. Chats & shop: nothing.
export const BootLoader = ({ ready }) => {
  const { pathname } = useLocation();
  const [counts, setCounts] = useState(loaderRegistry.counts());
  const [phase, setPhase] = useState('boot'); // boot | hidden | show | exit
  const [isBoot, setIsBoot] = useState(true);
  const [sceneKey, setSceneKey] = useState(0);
  const [poofOnly, setPoofOnly] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [spent, setSpent] = useState(false); // poof already played for this loading stretch
  const shownAt = useRef(0);

  useEffect(() => loaderRegistry.subscribe(setCounts), []);

  const quiet = QUIET_ROUTES.some((p) => pathname.startsWith(p));
  const fullMode = !ready || counts.full > 0;
  const busy = fullMode || (!quiet && counts.poof > 0);

  useEffect(() => { if (!busy) setSpent(false); }, [busy]);

  // Start a sequence once loading lasts longer than the anti-flicker delay
  useEffect(() => {
    if (!busy || (spent && !fullMode) || (phase !== 'boot' && phase !== 'hidden')) return;
    const t = setTimeout(() => {
      shownAt.current = Date.now();
      setPoofOnly(!fullMode);
      setCleared(false);
      setSceneKey((k) => k + 1);
      setPhase('show');
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [busy, spent, phase, fullMode]);

  // Poof-only pages: capsule pops, smoke clears into the page — regardless of loading
  useEffect(() => {
    if (phase !== 'show' || !poofOnly || fullMode) return;
    const elapsed = Date.now() - shownAt.current;
    const t1 = setTimeout(() => setCleared(true), Math.max(0, POP_MS + 100 - elapsed));
    const t2 = setTimeout(() => { setSpent(true); setPhase('exit'); }, Math.max(0, POP_MS + POOF_CLEAR_MS - elapsed));
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [phase, poofOnly, fullMode]);

  // Full mode: finished before the pop → poof into the page; otherwise the screen is up, fade it out
  useEffect(() => {
    if (busy) return;
    if (phase === 'boot') { setPhase('exit'); return; }
    if (phase !== 'show' || poofOnly) return;
    const elapsed = Date.now() - shownAt.current;
    if (elapsed < POP_MS + 80) {
      setPoofOnly(true);
      return;
    }
    const t = setTimeout(() => setPhase('exit'), SETTLE_MS);
    return () => clearTimeout(t);
  }, [busy, phase, poofOnly]);

  // Full-mode sequence that converted to poof-only after loading finished
  useEffect(() => {
    if (phase !== 'show' || !poofOnly || !fullMode || busy) return;
    const elapsed = Date.now() - shownAt.current;
    const t1 = setTimeout(() => setCleared(true), Math.max(0, POP_MS + 100 - elapsed));
    const t2 = setTimeout(() => setPhase('exit'), Math.max(0, POP_MS + POOF_CLEAR_MS - elapsed));
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [phase, poofOnly, fullMode, busy]);

  // A full-screen page (Library/episode) started loading during a quick poof: upgrade to the Capsule Corp screen
  useEffect(() => {
    if (!fullMode || !busy || !poofOnly || (phase !== 'show' && phase !== 'exit')) return;
    setPoofOnly(false);
    setCleared(false);
    setPhase('show');
  }, [fullMode, busy, poofOnly, phase]);

  useEffect(() => {
    if (phase !== 'exit') return;
    const t = setTimeout(() => { setPhase('hidden'); setIsBoot(false); }, EXIT_FADE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'hidden') return null;
  return (
    <div
      className={`cc-overlay ${isBoot ? 'cc-overlay-boot' : 'cc-overlay-page'} ${poofOnly ? 'cc-poof-only' : ''} ${cleared ? 'cc-clear' : ''} ${phase === 'exit' ? 'cc-exit' : ''}`}
      data-testid="boot-loader"
      aria-hidden={phase === 'exit'}
    >
      {phase !== 'boot' && <CapsuleScene key={sceneKey} deploy />}
    </div>
  );
};

export default BootLoader;
